import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  Popper,
  Grow,
  Paper,
  MenuList,
  ClickAwayListener,
  Tooltip,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import StarIcon from '@mui/icons-material/Star';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translations, Navigation as NavigationTranslations } from '../translations';

const pages = [
  { name: 'home', path: '/' },
  { name: 'diensten', path: '/diensten' },
  { name: 'events', path: '/events' },
  { name: 'tips', path: '/tips' },
  { name: 'over', path: '/over' },
  { name: 'contact', path: '/contact' }
];

const serviceLinks = [
  { name: 'individueleCoaching', path: '/diensten/individuele-coaching' },
  { name: 'zelfcoaching', path: '/diensten/zelfcoaching' },
  { name: 'workshops', path: '/diensten/workshops' },
  { name: 'lesgeven', path: '/diensten/lesgeven' },
];

const overLinks = [
  { name: 'overMij', path: '/over' },
  { name: 'waaromik', path: '/waarom-ik' },
];

const NavigationComponent = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [openServicesMenu, setOpenServicesMenu] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState<null | HTMLElement>(null);
  const [openOverMenu, setOpenOverMenu] = useState(false);
  const [overAnchorEl, setOverAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isPremiumUser, currentUser, logout } = useAuth();
  const { language, setLanguage } = useLanguage();
  const tNav = translations[language].navigation as NavigationTranslations;

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleServicesMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setServicesAnchorEl(event.currentTarget);
    setOpenServicesMenu(true);
  };

  const handleServicesMouseLeave = () => {
    setTimeout(() => {
        if (!servicesAnchorEl?.querySelector(':hover')) {
             setOpenServicesMenu(false);
        }
    }, 150);
  };

  const handleServicesMenuClose = () => {
    setOpenServicesMenu(false);
    setServicesAnchorEl(null);
  };

  const handleOverMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setOverAnchorEl(event.currentTarget);
    setOpenOverMenu(true);
  };

  const handleOverMouseLeave = () => {
    setTimeout(() => {
        if (!overAnchorEl?.querySelector(':hover')) {
             setOpenOverMenu(false);
        }
    }, 150);
  };

  const handleOverMenuClose = () => {
    setOpenOverMenu(false);
    setOverAnchorEl(null);
  };

  const handleLanguageChange = (lang: 'nl' | 'en') => {
    setLanguage(lang);
  };

  const handleLogout = async () => {
    try {
        await logout();
        if (anchorElNav) {
            handleCloseNavMenu();
        }
        navigate('/');
    } catch (error) {
        console.error("Logout failed:", error);
    }
  };

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/diensten' && location.pathname.startsWith('/diensten')) {
       return true;
    }
    if (path === '/over' && (location.pathname === '/over' || location.pathname === '/waarom-ik')) {
       return true;
    }
    return location.pathname === path;
  };

  const isLoggedIn = !!currentUser;
  const hasPremium = isPremiumUser();

  const menuItemStyle = (path: string) => ({
    py: 1,
    px: 2,
    my: 0.5,
    borderRadius: 1,
    borderLeft: '3px solid transparent',
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    color: 'inherit',
    fontWeight: 'inherit',
    '&.Mui-selected': {
      borderLeft: '3px solid',
      borderColor: theme.palette.primary.main,
      backgroundColor: 'transparent',
      fontWeight: 600,
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      }
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    }
  });

  const navButtonStyle = (path: string) => ({
    mx: 0.5,
    px: 2,
    py: 0.5,
    color: isActivePath(path) ? theme.palette.primary.main : theme.palette.text.primary,
    fontSize: '0.95rem',
    fontWeight: isActivePath(path) ? 600 : 400,
    textTransform: 'none',
    letterSpacing: '0.2px',
    borderRadius: '8px',
    backgroundColor: isActivePath(path) ? 'rgba(139, 125, 107, 0.08)' : 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(139, 125, 107, 0.05)',
      color: theme.palette.primary.dark,
    }
  });

  // Renamed for clarity: Renders only DESKTOP user elements
  const renderDesktopUserMenu = () => {
    // --- Logged Out State (Desktop) ---
    if (!isLoggedIn) {
      return (
          <Button 
            variant="contained" 
            color="primary"
            component={RouterLink} 
            to="/login" 
            startIcon={<LoginIcon />}
            sx={{ 
              borderRadius: 2,
              fontWeight: 500,
              py: 0.7,
              textTransform: 'none',
            }}
          >
            {language === 'nl' ? 'Inloggen' : 'Login'}
          </Button>
      );
    }

    // --- Logged In State (Desktop) ---
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Button component={RouterLink} to="/afspraak" size="small" sx={{ color: 'text.secondary' }}>
          {language === 'nl' ? 'Mijn Portaal' : 'My Portal'}
        </Button>
        <Button component={RouterLink} to="/mijn-content" size="small" sx={{ color: 'text.secondary' }}>
          {language === 'nl' ? 'Mijn Content' : 'My Content'}
        </Button>
        {hasPremium && (
          <Chip
            icon={<StarIcon sx={{ color: 'white !important' }} />}
            label={language === 'nl' ? "Premium" : "Premium"}
            color="secondary"
            size="small"
            sx={{ mr: 0.5 }}
          />
        )}
         <Tooltip title={language === 'nl' ? `Account (${currentUser.displayName || currentUser.email || 'Gebruiker'})` : `Account (${currentUser.displayName || currentUser.email || 'User'})`}>
            <IconButton
                component={RouterLink}
                to="/afspraak"
                sx={{ p: 0 }}
            >
                 {/* Avatar logic remains the same */}
                 {currentUser?.photoURL ? (
                     <Avatar 
                         src={currentUser.photoURL} 
                         alt={currentUser.displayName || 'User'} 
                         sx={{ width: 32, height: 32, bgcolor: hasPremium ? 'secondary.main' : 'primary.main', border: hasPremium ? '2px solid' : 'none', borderColor: 'secondary.main' }} 
                     />
                 ) : (
                     <Avatar sx={{ width: 32, height: 32, bgcolor: hasPremium ? 'secondary.main' : 'primary.main', border: hasPremium ? '2px solid' : 'none', borderColor: 'secondary.main' }}>
                         {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                     </Avatar>
                 )}
            </IconButton>
        </Tooltip>
        <Tooltip title={language === 'nl' ? 'Uitloggen' : 'Logout'}>
          <IconButton 
            onClick={handleLogout}
            size="small"
            sx={{ 
              color: 'text.secondary', 
              '&:hover': { bgcolor: 'action.hover' } 
            }}
          >
            <LogoutIcon fontSize="small"/>
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // Language switcher menu
  const renderLanguageMenu = () => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={language === 'nl' ? 'Switch to English' : 'Wissel naar Nederlands'}>
          <Button
            onClick={() => handleLanguageChange(language === 'nl' ? 'en' : 'nl')}
            size="small"
            aria-label={language === 'nl' ? 'Switch to English' : 'Wissel naar Nederlands'}
            sx={{ 
              minWidth: '45px',
              borderRadius: 2, 
              px: 1.5, 
              py: 0.6,
              fontWeight: 'bold',
              fontSize: '0.8rem',
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'action.disabled'
              }
            }}
          >
            {language === 'nl' ? 'EN' : 'NL'}
          </Button>
        </Tooltip>
      </Box>
    );
  };

  const renderOverMenu = () => (
    <Paper 
      elevation={3}
      sx={{ 
        mt: 1,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: theme.shadows[3]
      }}
    >
      <ClickAwayListener onClickAway={handleOverMenuClose}>
        <MenuList autoFocusItem={openOverMenu} id="over-menu-popper" onMouseLeave={handleOverMouseLeave}> 
          {overLinks.map((link) => (
            <MenuItem
              key={link.path}
              component={RouterLink}
              to={link.path}
              onClick={handleOverMenuClose}
              sx={{
                ...menuItemStyle(link.path),
              }}
              selected={location.pathname === link.path}
            >
              {tNav[link.name]} 
            </MenuItem>
          ))}
        </MenuList>
      </ClickAwayListener>
    </Paper>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        height: '80px'
      }}
    >
      <Container maxWidth="xl" sx={{ pl: { md: 0 } }}>
        <Toolbar
          disableGutters
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '80px',
            height: '80px',
            px: { xs: 2, md: 2 }
          }}
        >
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', width: '220px', ml: 0.25 }}>
            <RouterLink to="/">
              <img
                src="/logo_sterkaanhetwerk.jpg"
                alt="Sterk aan het Werk Logo"
                style={{ 
                    height: '120px',
                    width: 'auto',
                    display: 'block',
                    marginTop: '15px',
                    borderRadius: '0 0 12px 12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                 }}
              />
            </RouterLink>
          </Box>

          {/* Box for Central Navigation Links */}
          <Box sx={{ 
            flexGrow: 1, 
            display: { xs: 'none', md: 'flex' }, 
            justifyContent: 'center',
            gap: 3.5,
          }}>
            {pages.map((item) => {
              if (item.name === 'contact') {
                return (
                  <Button
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    sx={navButtonStyle(item.path)}
                  >
                    {tNav[item.name]}
                  </Button>
                );
              }
              if (item.name === 'diensten') {
                return (
                  <Box 
                    key={item.path} 
                    onMouseLeave={handleServicesMouseLeave}
                    sx={{ position: 'relative' }}
                  >
                    <Button
                      component={RouterLink}
                      to="/diensten"
                      onClick={handleServicesMenuClose}
                      onMouseEnter={handleServicesMouseEnter}
                      aria-owns={openServicesMenu ? 'services-menu-popper' : undefined}
                      aria-haspopup="true"
                      sx={{
                        ...navButtonStyle('/diensten'),
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {tNav[item.name]}
                      <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: '1.2rem' }} />
                    </Button>
                    <Popper
                      open={openServicesMenu}
                      anchorEl={servicesAnchorEl}
                      placement="bottom-start"
                      transition
                      disablePortal
                      sx={{ zIndex: theme.zIndex.appBar + 1 }}
                      onMouseLeave={handleServicesMouseLeave}
                    >
                      {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                          <Paper 
                            elevation={3}
                            sx={{ 
                              mt: 1,
                              borderRadius: 2,
                              overflow: 'hidden',
                              boxShadow: theme.shadows[3]
                            }}
                          >
                            <ClickAwayListener onClickAway={handleServicesMenuClose}>
                              <MenuList autoFocusItem={openServicesMenu} id="services-menu-popper">
                                {serviceLinks.map((dienst) => (
                                  <MenuItem
                                    key={dienst.path}
                                    component={RouterLink}
                                    to={dienst.path}
                                    onClick={handleServicesMenuClose}
                                    sx={menuItemStyle(dienst.path)}
                                    selected={location.pathname === dienst.path}
                                  >
                                    {translations[language].services[dienst.name]}
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </Box>
                );
              }
              if (item.name === 'over') {
                 return (
                  <Box 
                    key={item.path} 
                    onMouseLeave={handleOverMouseLeave}
                    sx={{ position: 'relative' }}
                  >
                    <Button
                      component={RouterLink}
                      to="/over"
                      onClick={handleOverMenuClose}
                      onMouseEnter={handleOverMouseEnter}
                      aria-owns={openOverMenu ? 'over-menu-popper' : undefined}
                      aria-haspopup="true"
                      sx={{
                        ...navButtonStyle('/over'),
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {tNav[item.name]}
                      <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: '1.2rem' }} />
                    </Button>
                    <Popper
                      open={openOverMenu}
                      anchorEl={overAnchorEl}
                      placement="bottom-start"
                      transition
                      disablePortal
                      sx={{ zIndex: theme.zIndex.appBar + 1 }}
                      onMouseLeave={handleOverMouseLeave}
                    >
                       {({ TransitionProps }) => (
                         <Grow {...TransitionProps}>
                          {renderOverMenu()}
                        </Grow>
                      )}
                    </Popper>
                  </Box>
                );
              }
              return (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={navButtonStyle(item.path)}
                >
                  {tNav[item.name]}
                </Button>
              );
            })}
          </Box>

          {/* Container for right-aligned buttons */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            alignItems: 'center',
            gap: 1.5,
          }}>
            {renderDesktopUserMenu()}
            {renderLanguageMenu()}
          </Box>

          {/* --- Mobile View Container (Now a direct child of Toolbar) --- */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' }, 
            alignItems: 'center', 
            width: '100%' 
          }}>
            {/* Left Item: Menu Icon */}
              <IconButton
                size="large"
                aria-label="navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                 color="inherit" // Explicitly set color
                 sx={{ mr: 1 }} // Add some margin if needed
              >
                <MenuIcon />
              </IconButton>
              
               {/* Optional: Add Logo for Mobile if desired */}
               {/* <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
                 <RouterLink to="/">
                   <img src="/logo_small.png" alt="Logo" style={{ height: 40 }} />
                 </RouterLink>
               </Box> */}
               <Box sx={{ flexGrow: 1 }} /> {/* Pushes right items */}

              {/* Right Items Group: Language + Login/Portal Icons */}
               <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}> {/* Adjusted gap */}
                {renderLanguageMenu()}
              
                 {/* Conditional User Icons for Mobile Top Bar */} 
                {!isLoggedIn ? (
                  // Logged Out: Show Login Icon
                  <Tooltip title={language === 'nl' ? 'Inloggen' : 'Login'}>
                    <IconButton component={RouterLink} to="/login" color="inherit" size="small">
                      <LoginIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  // Logged In: Show Portal, Content, Avatar, Logout Icons
                  <>
                    <Tooltip title={language === 'nl' ? 'Mijn Content' : 'My Content'}>
                      <IconButton component={RouterLink} to="/mijn-content" color="inherit" size="small">
                        <WorkspacePremiumIcon fontSize="small"/>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={language === 'nl' ? 'Mijn Portaal' : 'My Portal'}>
                      <IconButton component={RouterLink} to="/afspraak" color="inherit" size="small">
                        <LibraryBooksIcon fontSize="small"/>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={language === 'nl' ? `Account (${currentUser.displayName || currentUser.email || 'Gebruiker'})` : `Account (${currentUser.displayName || currentUser.email || 'User'})`}>
                      <IconButton component={RouterLink} to="/afspraak" sx={{ p: 0 }} size="small">
                         {/* Avatar Logic */} 
                         {currentUser?.photoURL ? (
                             <Avatar src={currentUser.photoURL} alt={currentUser.displayName || 'User'} sx={{ width: 28, height: 28, bgcolor: hasPremium ? 'secondary.main' : 'primary.main', border: hasPremium ? '2px solid' : 'none', borderColor: 'secondary.main' }} />
                         ) : (
                             <Avatar sx={{ width: 28, height: 28, bgcolor: hasPremium ? 'secondary.main' : 'primary.main', border: hasPremium ? '2px solid' : 'none', borderColor: 'secondary.main' }}>
                                 {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                             </Avatar>
                         )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={language === 'nl' ? 'Uitloggen' : 'Logout'}>
                      <IconButton onClick={handleLogout} size="small" color="inherit">
                        <LogoutIcon fontSize="small"/>
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>

              {/* Mobile Menu Definition (Remains the same, triggered by MenuIcon) */}
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left', // Adjusted anchor to align with left icon
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left', // Adjusted transform origin
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                  '& .MuiPaper-root': {
                    width: '100%', 
                    maxWidth: 300, 
                    mt: 1.5, 
                    boxShadow: theme.shadows[3]
                  }
                }}
              >
                {pages.map((item) => {
                   if (item.name === 'diensten') {
                     return (
                       <Box key={item.name}>
                         <MenuItem 
                           component={RouterLink}
                           to={item.path}
                           onClick={handleCloseNavMenu} 
                           sx={menuItemStyle(item.path)}
                           selected={isActivePath(item.path) && !serviceLinks.some(sl => location.pathname === sl.path)}
                         >
                           {tNav[item.name]}
                         </MenuItem>
                         <Box sx={{ pl: 2 }}>
                           {serviceLinks.map((dienst) => (
                             <MenuItem
                               key={dienst.path}
                               component={RouterLink}
                               to={dienst.path}
                               onClick={handleCloseNavMenu}
                               sx={menuItemStyle(dienst.path)}
                               selected={location.pathname === dienst.path}
                             >
                               {translations[language].services[dienst.name]}
                             </MenuItem>
                           ))}
                         </Box>
                       </Box>
                     );
                   }
                   if (item.name === 'over') {
                     return (
                       <Box key={item.name}>
                         {/* Main "Over" item */}
                         <MenuItem 
                           component={RouterLink}
                           to={item.path} // Link to /over
                           onClick={handleCloseNavMenu} 
                           sx={menuItemStyle(item.path)}
                           // Selected ONLY if on /over, not its sub-pages
                           selected={location.pathname === item.path} 
                         >
                           {tNav[item.name]}
                         </MenuItem>
                         {/* Sub-items indented */}
                         <Box sx={{ pl: 2 }}> 
                           {overLinks.map((link) => (
                             <MenuItem
                               key={link.path}
                               component={RouterLink}
                               to={link.path}
                               onClick={handleCloseNavMenu}
                               sx={{
                                 ...menuItemStyle(link.path),
                               }}
                               selected={location.pathname === link.path}
                             >
                               {tNav[link.name]}
                             </MenuItem>
                           ))}
                         </Box>
                       </Box>
                     );
                   }
                   return (
                    <MenuItem 
                      key={item.path} 
                      component={RouterLink}
                      to={item.path}
                      onClick={handleCloseNavMenu} 
                      sx={menuItemStyle(item.path)}
                      selected={isActivePath(item.path)}
                    >
                      {tNav[item.name]}
                    </MenuItem>
                  );
                })}
                <Divider sx={{ my: 1 }} />
                
                {/* Display specific user actions in mobile menu */}
                {!isLoggedIn ? (
                    // Login/Register prompt
                    <MenuItem component={RouterLink} to="/login" onClick={handleCloseNavMenu} sx={menuItemStyle('/login')}>
                        <ListItemIcon><LoginIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>{language === 'nl' ? 'Inloggen / Registreren' : 'Login / Register'}</ListItemText> 
                    </MenuItem>
                 ) : (
                    // Logged-in specific menu items
                    <Box>
                         {/* Keep Profile Link */}
                         <MenuItem component={RouterLink} to="/profiel" onClick={handleCloseNavMenu} sx={menuItemStyle('/profiel')}>
                            <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>{language === 'nl' ? 'Mijn Profiel' : 'My Profile'}</ListItemText>
                        </MenuItem>
                        {/* REMOVED Mijn Portaal and Mijn Content - Now Icons in Top Bar */}
                        {/* 
                        <MenuItem component={RouterLink} to="/afspraak" onClick={handleCloseNavMenu} sx={menuItemStyle('/afspraak')}>
                            <ListItemIcon><LibraryBooksIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>{language === 'nl' ? 'Mijn Portaal' : 'My Portal'}</ListItemText>
                        </MenuItem>
                        <MenuItem component={RouterLink} to="/mijn-content" onClick={handleCloseNavMenu} sx={menuItemStyle('/mijn-content')}>
                            <ListItemIcon><WorkspacePremiumIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>{language === 'nl' ? 'Mijn Content' : 'My Content'}</ListItemText>
                        </MenuItem> 
                        */}
                        <Divider sx={{ my: 1 }} />
                        {/* Keep Logout Button */}
                        <MenuItem onClick={() => { handleLogout(); handleCloseNavMenu(); }}>
                            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>{language === 'nl' ? 'Uitloggen' : 'Logout'}</ListItemText>
                        </MenuItem>
                    </Box>
                 )}
              </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default NavigationComponent; 